import { isAxiosError } from "axios";
import {
  CopilotBlock,
  CopilotReader,
  ParamsGetConfigCopilotVerdict,
  PayloadPostConfigCopilotBrief,
  PayloadPostConfigCopilotReadout,
  PayloadPostConfigCopilotTurn,
  ResponseGetConfigCopilotVerdict,
  ResponsePostConfigCopilotBrief,
  ResponsePostConfigCopilotReadout,
  ResponsePostConfigCopilotTurn,
  RouteGetConfigCopilotVerdict,
  RoutePostConfigCopilotBrief,
  RoutePostConfigCopilotReadout,
  RoutePostConfigCopilotTurn,
} from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import {
  ErrorPostMerchflowQaRateLimited,
  MerchflowQaAnswerIdHeader,
  PayloadPostMerchflowQa,
  PayloadPostMerchflowQaFeedback,
  ResponsePostMerchflowQa,
  RoutePostMerchflowQa,
  RoutePostMerchflowQaFeedback,
} from "@CommonApi/merchflows/_merchflowId_/qa";
import { API, getRequest, postRequest } from "src/utils/axios";

export const TURN_TIMEOUT_MS = 6000;

/** Give the backend's 30-second answer budget room for page lookup and transport. */
export const QA_TIMEOUT_MS = 35000;

// Matches owa-backend ConfigCopilotController until omni-common carries the route.
export const RoutePostConfigCopilotOpening = "/merchflows/:merchflowId/config-copilot/opening";

export type PayloadPostConfigCopilotOpening = PayloadPostConfigCopilotBrief;

/** The brief as owa-backend sends it, until omni-common carries these fields. */
export interface ConfigCopilotBrief extends ResponsePostConfigCopilotBrief {
  /** The base's first question, asked once above its chips (`asks`); absent where it asks nothing. */
  question?: string;
}

/** The opening's words, by the brief's own ids; a piece left out keeps the brief's. */
export interface ResponsePostConfigCopilotOpening {
  reader: CopilotReader;
  headline?: string;
  observations?: { id: string; line: string }[];
  setup?: { label: string; reason: string }[];
  question?: string;
  /** A change of setup the model made, as a turn returns one: its line and its patch. */
  blocks?: CopilotBlock[];
}

export class OUnavailableError extends Error {
  constructor() {
    super("O_UNAVAILABLE");
    this.name = "OUnavailableError";
  }
}

export class ORateLimitedError extends Error {
  constructor(readonly retryAfterSeconds: number) {
    super("O_QA_RATE_LIMITED");
    this.name = "ORateLimitedError";
  }
}

const urlOf = (route: string, merchflowId: number) =>
  `${API}${route.replace(":merchflowId", String(merchflowId))}`;

const isUnavailable = (error: unknown) =>
  isAxiosError<{ code?: string }>(error) &&
  error.response?.status === 503 &&
  error.response.data?.code === "O_UNAVAILABLE";

export const postConfigCopilotBrief = async (
  merchflowId: number,
  payload: PayloadPostConfigCopilotBrief,
) => {
  const { data } = await postRequest<ConfigCopilotBrief>(
    urlOf(RoutePostConfigCopilotBrief, merchflowId),
    { payload },
  );
  return data;
};

export const postConfigCopilotOpening = async (
  merchflowId: number,
  payload: PayloadPostConfigCopilotOpening,
) => {
  const { data } = await postRequest<ResponsePostConfigCopilotOpening>(
    urlOf(RoutePostConfigCopilotOpening, merchflowId),
    { payload },
  );
  return data;
};

// Settles as the request does, or as O unavailable once `timeoutMs` passes.
const withinTurnTime = async <T>(request: Promise<T>, timeoutMs: number) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new OUnavailableError()), timeoutMs);
  });
  try {
    return await Promise.race([request, timeout]);
  } catch (error) {
    throw isUnavailable(error) ? new OUnavailableError() : error;
  } finally {
    clearTimeout(timer);
  }
};

export const postConfigCopilotTurn = async (
  merchflowId: number,
  payload: PayloadPostConfigCopilotTurn,
  timeoutMs = TURN_TIMEOUT_MS,
) => {
  const { data } = await withinTurnTime(
    postRequest<ResponsePostConfigCopilotTurn>(urlOf(RoutePostConfigCopilotTurn, merchflowId), {
      payload,
    }),
    timeoutMs,
  );
  return data;
};

export const postMerchflowQa = async (
  merchflowId: number,
  payload: PayloadPostMerchflowQa,
  timeoutMs = QA_TIMEOUT_MS,
) => {
  try {
    const { data, headers } = await withinTurnTime(
      postRequest<ResponsePostMerchflowQa>(urlOf(RoutePostMerchflowQa, merchflowId), {
        payload,
      }),
      timeoutMs,
    );
    const rawAnswerId: unknown = headers?.[MerchflowQaAnswerIdHeader];
    return {
      ...data,
      answerId: typeof rawAnswerId === "string" ? rawAnswerId : undefined,
    };
  } catch (error) {
    if (
      isAxiosError<ErrorPostMerchflowQaRateLimited>(error) &&
      error.response?.status === 429 &&
      error.response.data?.code === "O_QA_RATE_LIMITED"
    ) {
      const header: unknown = error.response.headers?.["retry-after"];
      const bodySeconds = Number(error.response.data.retryAfterSeconds);
      const headerSeconds = Number(header);
      const seconds =
        Number.isFinite(bodySeconds) && bodySeconds > 0
          ? bodySeconds
          : Number.isFinite(headerSeconds) && headerSeconds > 0
            ? headerSeconds
            : 1;
      throw new ORateLimitedError(Math.min(3600, Math.max(1, Math.ceil(seconds))));
    }
    throw error;
  }
};

/** A thumbs up or down on one of O's answers. 204, no body. */
export const postMerchflowQaFeedback = async (
  merchflowId: number,
  payload: PayloadPostMerchflowQaFeedback,
) => {
  await postRequest<void>(urlOf(RoutePostMerchflowQaFeedback, merchflowId), { payload });
};

export const postConfigCopilotReadout = async (
  merchflowId: number,
  payload: PayloadPostConfigCopilotReadout,
) => {
  const { data } = await postRequest<ResponsePostConfigCopilotReadout>(
    urlOf(RoutePostConfigCopilotReadout, merchflowId),
    { payload },
  );
  return data;
};

export const getConfigCopilotVerdict = async (
  merchflowId: number,
  queryParams: ParamsGetConfigCopilotVerdict,
) => {
  const { data } = await getRequest<ResponseGetConfigCopilotVerdict>(
    urlOf(RouteGetConfigCopilotVerdict, merchflowId),
    { queryParams },
  );
  return data;
};
